import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFlag } from '@rsos/flags';
import { GPX_FEATURES } from '@rsos/gainsight';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import {
  useLazyGetConversationMetadataQuery,
  useLazyCreateConversationQuery,
  useLazyEndConversationQuery,
  useCreateConversationOutboundSMSMutation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import VTIconsRow from '../VideoTo911/VTIconsRow';
import { trackStartTF911Response } from '../textFrom911Trackings';
import { TF911Container } from './TF911.styles';
import { AbsoluteAllMessagesContainer } from './TF911.styles';
import TF911Chat from './TF911Chat';
import { trackCloseAllMessagesModal } from './TF911ChatBoxTrackings';
import TF911ChatHeader from './TF911ChatHeader';

/**
 * @param {object} props
 * @param {boolean} props.isTextFrom911ChatBoxVisible - if TF911 is visible to user (used for styling)
 * @param {function} props.onCollapseChat - func to trigger callback when chat box is collapsed
 */

const TF911ChatBox = ({
  isTextFrom911ChatBoxVisible = false,
  onCollapseChat = () => {},
}) => {
  const [showPreconfiguredMessages, setShowPreconfiguredMessages] =
    useState(false);
  const [isAllMessagesVisible, setIsAllMessagesVisible] = useState(false);
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();
  const [selectedPreconfiguredMessage, setSelectedPreconfiguredMessage] =
    useState(null);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasLocationsToRender = useHasLocationsToRender();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const conversationID = useSelectedTextFrom911ConversationID(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isOutboundSMSConversation = useMemo(
    () => conversation?.data?.isOutboundSMS,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const isVT911Enabled = useIsVT911Enabled();

  const [triggerGetConversationMetaData, getConversationMetaDataResult] =
    useLazyGetConversationMetadataQuery();

  const [triggerCreateConversation, createConversationResult] =
    useLazyCreateConversationQuery();

  const [triggerEndConversation] = useLazyEndConversationQuery();

  const [
    triggerCreateOutboundSMSConversation,
    createOutboundSMSConversationResult,
  ] = useCreateConversationOutboundSMSMutation();

  const preconfiguredMessages = useSelector(
    state => state.preconfiguredMessages?.messages,
  );
  const enabledPreconfiguredMessages = preconfiguredMessages?.filter(
    message => message.enabled,
  );

  useEffect(
    () => {
      if (selectedCallerID) {
        triggerGetConversationMetaData({
          callerID: selectedCallerID,
          orgName,
          custom: { isOutboundSMS: isOutboundSMSConversation },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCallerID],
  );

  // track start TF911 response
  useEffect(() => {
    if (['fulfilled', 'rejected'].includes(createConversationResult.status)) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911,
        responseSuccess: createConversationResult.status === 'fulfilled',
        conversationId: createConversationResult?.data?.conversation_id,
      });
    }
  }, [createConversationResult]);

  useEffect(() => {
    if (
      ['fulfilled', 'rejected'].includes(
        createOutboundSMSConversationResult.status,
      )
    ) {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911_OUTBOUND,
        responseSuccess:
          createOutboundSMSConversationResult.status === 'fulfilled',
        conversationId:
          createOutboundSMSConversationResult?.data?.conversation_id,
      });
    }
  }, [createOutboundSMSConversationResult]);

  const handleActionButtonClick = () => {
    onCollapseChat('tf911');
  };

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
    trackCloseAllMessagesModal(conversationID);
  };

  const handleCopyToChat = selectedMessage => {
    setIsAllMessagesVisible(false);
    setSelectedPreconfiguredMessage(selectedMessage);
  };

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const handleEndConversation = useCallback(
    callerID => {
      triggerEndConversation({ callerID, orgName });
    },
    [orgName, triggerEndConversation],
  );

  const handleStartConversation = useCallback(
    message => {
      if (!hasLocationsToRender) {
        return triggerCreateOutboundSMSConversation({
          callerID: selectedCallerID,
          message,
          orgName,
          custom: {},
        }).then(response => {
          if (response?.data?.conversation_id) {
            setShowPreconfiguredMessages(false);
          } else {
            if (response.error?.status === 409) {
              return triggerGetConversationMetaData({
                callerID: selectedCallerID,
                orgName,
                custom: { isOutboundSMS: true },
              }).then(response => {
                if (response?.isSuccess) {
                  setShowPreconfiguredMessages(false);
                  return response;
                } else {
                  // TODO: show toast err
                }
              });
            } else {
              // TODO: show toast err
            }
          }
          return response;
        });
      }
      return triggerCreateConversation({
        callerID: selectedCallerID,
        orgName,
        startMessage: message,
      }).then(response => {
        if (response.isSuccess) {
          setShowPreconfiguredMessages(false);
        }
        return response;
      });
    },
    [
      orgName,
      selectedCallerID,
      triggerCreateConversation,
      triggerCreateOutboundSMSConversation,
      triggerGetConversationMetaData,
      hasLocationsToRender,
    ],
  );

  const showStartChat = useMemo(() => {
    return !conversationID || isConversationEnded;
  }, [conversationID, isConversationEnded]);

  const showChatVTHeight =
    isVT911Enabled && (!isOutboundSMSConversation || hasLocationsToRender);

  return (
    <TF911Container
      data-name="text-from-911-container"
      flex={isTextFrom911ChatBoxVisible ? '1 1 auto' : '0 0 auto'}
    >
      <TF911ChatHeader
        isChatBoxVisible={isTextFrom911ChatBoxVisible}
        onActionButtonClick={handleActionButtonClick}
        onClickShowAccordion={handleClickShowAccordion}
        onEndConversation={handleEndConversation}
        onStartConversation={handleStartConversation}
        showPreconfiguredMessages={showPreconfiguredMessages}
        showStartChat={showStartChat}
        isOutboundSMSConversation={isOutboundSMSConversation}
        gpxFeature={
          isOutboundSMSConversation
            ? GPX_FEATURES.TF911_OUTBOUND
            : GPX_FEATURES.TF911
        }
      />

      {!hasAccessToUniteEventPane && (
        <VTIconsRow
          isChatBoxVisible={isTextFrom911ChatBoxVisible}
          onStartConversation={handleStartConversation}
        />
      )}

      {getConversationMetaDataResult.status === 'fulfilled' && (
        <TF911Chat
          callerID={selectedCallerID}
          showPreconfiguredMessages={showPreconfiguredMessages}
          isTextFrom911ChatBoxVisible={isTextFrom911ChatBoxVisible}
          showChatVTHeight={showChatVTHeight}
          setIsAllMessagesVisible={setIsAllMessagesVisible}
          preconfiguredMessages={enabledPreconfiguredMessages}
          selectedPreconfiguredMessage={selectedPreconfiguredMessage}
          setSelectedPreconfiguredMessage={setSelectedPreconfiguredMessage}
        />
      )}
      {isAllMessagesVisible && (
        <AbsoluteAllMessagesContainer>
          <AllMessages
            preconfiguredMessages={enabledPreconfiguredMessages}
            onClose={handleViewClose}
            onCopyToChat={handleCopyToChat}
          />
        </AbsoluteAllMessagesContainer>
      )}
    </TF911Container>
  );
};

export default TF911ChatBox;
